<template>
  <section
      :id="epoch.epochType"
      :data-epoch-id="epoch.id"
      class="epoch"
  >
  <h2
        class="epoch__title"
        :class="{ active: isActive }"
        :style="{ fontSize: computedFontSize, lineHeight: computedLineHeight, marginTop: computedMarginTop, color: computedColor }"
        :id="'epoch-title-' + epoch.id"
        ref="titleRef"
        @click="onTitleClick"
        @keydown.space="onTitleKeydown"
        @keydown.enter="onTitleKeydown"
        tabindex="0"
    >
      {{ epoch.epochYearName }}
    </h2>

    <div class="epoch__moments">
      <template v-for="(item, idx) in visibleMoments" :key="item.urlSegment">
        <MomentMM
            v-show="renderedMomentIndexes.includes(idx)"
            :moment="item"
            :index="idx"
            :animateIn="isActive && renderedMomentIndexes.includes(idx)"
        />
      </template>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, nextTick, Ref, ref, toRef, onUnmounted, watch} from "vue";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useTimelineDom } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineDom";
import { useScrollPinning } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useScrollPinning";
import { readDocumentCssPx } from "@features/Pages/TimelineMapPage/_js/app/services/readDocumentCssPx";
import MomentMM from "../moment/MomentMM.vue";
import type { IEpoch } from "../../interfaces/IEpoch";

const props = defineProps<{ 
  epoch: IEpoch; 
  index: number; 
  activeIndex: number;
  timelineEl: HTMLElement | null;
}>();

const store = useTimelineMapStore();

const timelineRef = toRef(props, 'timelineEl') as unknown as Ref<HTMLElement | null>;
const { goToEpoch } = useTimelineDom(timelineRef);

let titleClickCooldown = false;
const { smoothScrollTo, readCssPx, stopPin } = useScrollPinning();

const renderedMomentIndexes = ref<number[]>([]);
const titleRef = ref<HTMLElement | null>(null);

const isActive = computed(() => store.activeEpochId === props.epoch.id);
const epochDistance = computed(() => props.activeIndex - props.index);

const visibleMoments = computed(() => {
  const items = props.epoch.timelineMapItems;
  const start = Math.max(0, props.epoch.startIndex ?? 0);
  const count = Math.min(items.length - start, props.epoch.visibleCount ?? items.length);
  return items.slice(start, start + count);
});

function fontStepForDistance(d: number, a: string, b: string, c: string) {
  // d = epochDistance from active epoch
  if (d === 0 || d === -1) { return a; }
  if (d === -2 || d === 1) { return b; }
  if (d === -3 || d === 2) { return c; }
  
  return "0px";
}

const computedFontSize = computed(() => fontStepForDistance(epochDistance.value, "26px", "22px", "16px"));
const computedLineHeight = computed(() => fontStepForDistance(epochDistance.value, "31px", "26px", "22px"));
const computedMarginTop = computed(() => (epochDistance.value === -1 ? "24px" : "0px"));

const computedColor = computed(() => { 
  const d = epochDistance.value;
  if (d === -1 || d === 1) { return "var(--color-bremon-100)"; }
  if (d === -2 || d === 2) { return "var(--color-storm-80)"; }
  if (d === -3) { return "var(--color-storm-100)"; }
  
  return "";
});

async function onTitleClick(e?: MouseEvent) {  
  if (titleClickCooldown) { return; }
  titleClickCooldown = true;
  setTimeout(() => (titleClickCooldown = false), 250);
  
  const titleElement = titleRef.value;
  if (!titleElement) { return; }

  if (store.isDragging || (e && e.button !== 0)) {
    e?.preventDefault();
    e?.stopImmediatePropagation?.();
    e?.stopPropagation();
    return;
  }

  const prevIdx = store.epochs.findIndex(e => e.id === store.activeEpochId);
  const nextIdx = store.epochs.findIndex(e => e.id === props.epoch.id);
  const movingDown = nextIdx > prevIdx;

  await goToEpoch(props.epoch.id);
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const downBias = movingDown ? readCssPx(".site--mm", "--scroll-down-bias", 18) : 0;
  
  const rect = titleElement.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight - downBias;
  await smoothScrollTo(targetTop, 700);
}

function onTitleKeydown(e: KeyboardEvent) {
  if (store.isDragging) { return; }
  
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onTitleClick();
  }
}

function revealMomentsSequentially() {
  renderedMomentIndexes.value = [];
  nextTick(() => {
    for (let i = 0; i < visibleMoments.value.length; i++) {
      setTimeout(() => { renderedMomentIndexes.value.push(i); }, i * 100);
    }
  });
}

onUnmounted(() => { stopPin(); });

watch(isActive, (nowActive) => {
  if (nowActive) {
    revealMomentsSequentially();
    } else {
        renderedMomentIndexes.value = [];
    }
  }, { immediate: true });
</script>
